import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { WorkOrder } from '../../features/workorder/components/model/workorder';
import { WorkOrderLineView } from '../../features/workorder/components/work-order-line-list/model/work-order-line-view.model';
import { WorkOrderFiltersModel } from '../../features/workorder/components/model/work-order-filters.model';
import { LocationInformationViewModel } from '../../features/workorder/components/work-order-line-list/model/location-information-view.model';
import { WorkOrderHeaderMetadata } from '../../features/workorder/components/work-order-line-list/model/paginated-wol.model';

export class UpdateCurrentPaginatedWorkOrdersAction {
  static readonly type = '[WorkOrder] Update Current Paginated WorkOrders';
  constructor(public workOrders: WorkOrder[], public recordCount: number) {}
}

export class ResetWorkOrderStateAction {
  static readonly type = '[WorkOrder] Reset WorkOrder State';
}

export class UnlockWorkOrderStateAction {
  static readonly type = '[WorkOrder] Unlock WorkOrder State';
}

export class UpdateCurrentPaginatedWorkOrderLinesAction {
  static readonly type = '[WorkOrderLine] Update Current Paginated WorkOrderLines';

  constructor(public workOrderLines: WorkOrderLineView[]) {}
}

export class UpdateWorkOrderHeaderMetadataAction {
  static readonly type = '[WorkOrder] Update WorkOrder Header Metadata';
  constructor(public workOrderId: string, public metadata: WorkOrderHeaderMetadata) {}
}

export class UpdateWorkOrderFiltersAction {
  static readonly type = '[WorkOrder] Update WorkOrder Filters and Paginator';
  constructor(public workOrderFilters: WorkOrderFiltersModel) {}
}

export class UpdateIsRestoreStateAction {
  static readonly type = '[WorkOrder] Update IsRestoreState';
  constructor(public isRestoreState: boolean) {}
}

export class UpdateLocationInformationAction {
  static readonly type = '[WorkOrderLine] Update IsLocationInformationLoaded';
  constructor(public locationInformation: LocationInformationViewModel) {}
}

export class ResetLocationInformationAction {
  static readonly type = '[WorkOrderLine] Reset Location Information';
}

export interface WorkOrderStateModel {
  loading: boolean;
  currentPaginatedWorkOrders: WorkOrder[];
  workOrderFilters?: WorkOrderFiltersModel;
  isRestoreState: boolean;
  totalRecords: number;
  currentWorkOrderLines: WorkOrderLineView[];
  workOrderHeaderMetadata: { [workOrderId: string]: WorkOrderHeaderMetadata }; // Store metadata separately by workOrderId
  selectedWorkOrderLine: {
    workOrderLine: WorkOrderLineView;
    isLocationInformationLoaded: boolean;
    locationInformation: LocationInformationViewModel;
  };
}

const defaultState: WorkOrderStateModel = {
  loading: true,
  currentPaginatedWorkOrders: [] as WorkOrder[],
  workOrderFilters: {
    workOrderNumber: '',
    workOrderStatus: [],
    workOrderPlant: [],
    workOrderSite: [],
    customerCode: '',
    customerName: '',
    crn: '',
    workOrderReceivedDateStart: '',
    workOrderReceivedDateEnd: '',
    workOrderDueDateStart: '',
    workOrderDueDateEnd: '',
    searchByUser: ''
  },
  isRestoreState: false,
  totalRecords: 0,
  currentWorkOrderLines: [] as WorkOrderLineView[],
  workOrderHeaderMetadata: {},
  selectedWorkOrderLine: {
    workOrderLine: null,
    isLocationInformationLoaded: false,
    locationInformation: null
  }
};

@State<WorkOrderStateModel>({
  name: 'workOrder',
  defaults: defaultState
})
@Injectable()
export class WorkOrderState {
  constructor() {}

  @Action(UpdateCurrentPaginatedWorkOrdersAction)
  setCurrentPaginatedWorkOrders(
    { patchState }: StateContext<WorkOrderStateModel>,
    { workOrders, recordCount }: UpdateCurrentPaginatedWorkOrdersAction
  ) {
    patchState({ currentPaginatedWorkOrders: workOrders, totalRecords: recordCount, loading: false });
  }

  @Action(ResetWorkOrderStateAction)
  resetWorkOrderState({ patchState }: StateContext<WorkOrderStateModel>) {
    patchState(defaultState);
  }

  @Action(UnlockWorkOrderStateAction)
  unlockWorkOrderState({ patchState }: StateContext<WorkOrderStateModel>) {
    patchState({ loading: true });
  }

  @Action(UpdateCurrentPaginatedWorkOrderLinesAction)
  setCurrentPaginatedWorkOrderLines(
    { patchState }: StateContext<WorkOrderStateModel>,
    { workOrderLines }: UpdateCurrentPaginatedWorkOrderLinesAction
  ) {
    patchState({ currentWorkOrderLines: workOrderLines });
  }

  @Action(UpdateWorkOrderHeaderMetadataAction)
  updateWorkOrderHeaderMetadata(
    { getState, patchState }: StateContext<WorkOrderStateModel>,
    { workOrderId, metadata }: UpdateWorkOrderHeaderMetadataAction
  ) {
    const state = getState();

    // Store metadata separately for cleaner architecture
    const updatedMetadata = {
      ...state.workOrderHeaderMetadata,
      [workOrderId]: {
        workOrderId,
        ...metadata
      }
    };

    // Update work order with only core data fields (not metadata flags)
    const updatedWorkOrders = state.currentPaginatedWorkOrders.map((wo) =>
      wo.id === workOrderId
        ? {
            ...wo,
            dueDayValue: metadata.dueDayValue,
            dueDayN: !metadata.showDueDayField, // Invert the logic as per JSP
            custCodeN: metadata.custCodeReqValue === 'N', // Show field if NOT 'N'
            custCodeLabelId: metadata.custCodeLabelId,
            fiscalIdNum: metadata.fiscalIdNum
          }
        : wo
    );

    patchState({
      currentPaginatedWorkOrders: updatedWorkOrders,
      workOrderHeaderMetadata: updatedMetadata
    });
  }

  @Action(UpdateWorkOrderFiltersAction)
  setWorkOrderFiltersAndPaginator({ patchState }: StateContext<WorkOrderStateModel>, { workOrderFilters }: UpdateWorkOrderFiltersAction) {
    patchState({ workOrderFilters });
  }

  @Action(UpdateIsRestoreStateAction)
  setIsRestoreState({ patchState }: StateContext<WorkOrderStateModel>, { isRestoreState }: UpdateIsRestoreStateAction) {
    patchState({ isRestoreState });
  }

  @Action(UpdateLocationInformationAction)
  setIsLocationInformationLoaded(
    { patchState }: StateContext<WorkOrderStateModel>,
    { locationInformation }: UpdateLocationInformationAction
  ) {
    patchState({
      selectedWorkOrderLine: {
        ...defaultState.selectedWorkOrderLine,
        isLocationInformationLoaded: true,
        locationInformation: locationInformation
      }
    });
  }

  @Action(ResetLocationInformationAction)
  resetLocationInformation({ patchState }: StateContext<WorkOrderStateModel>) {
    patchState({
      selectedWorkOrderLine: {
        ...defaultState.selectedWorkOrderLine,
        isLocationInformationLoaded: false,
        locationInformation: null
      }
    });
  }

  @Selector()
  static currentPaginatedWorkOrders(state: WorkOrderStateModel) {
    return state.currentPaginatedWorkOrders;
  }

  @Selector()
  static isLoading(state: WorkOrderStateModel) {
    return state.loading;
  }

  @Selector()
  static currentPaginatedWorkOrderLines(state: WorkOrderStateModel) {
    return state.currentWorkOrderLines;
  }

  @Selector()
  static workOrderFilters(state: WorkOrderStateModel) {
    return state.workOrderFilters;
  }

  @Selector()
  static isRestoreState(state: WorkOrderStateModel) {
    return state.isRestoreState;
  }

  @Selector()
  static totalRecords(state: WorkOrderStateModel) {
    return state.totalRecords;
  }

  @Selector()
  static isLocationInformationLoaded(state: WorkOrderStateModel) {
    return state.selectedWorkOrderLine?.isLocationInformationLoaded;
  }

  @Selector()
  static locationInformation(state: WorkOrderStateModel) {
    return state.selectedWorkOrderLine?.locationInformation;
  }

  @Selector()
  static getWorkOrderById(state: WorkOrderStateModel) {
    return (workOrderId: string) => state.currentPaginatedWorkOrders.find((wo) => wo.id === workOrderId);
  }

  @Selector()
  static getWorkOrderHeaderMetadata(state: WorkOrderStateModel) {
    return (workOrderId: string): WorkOrderHeaderMetadata | null => state.workOrderHeaderMetadata[workOrderId] || null;
  }
}
